import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';

import { authService } from '../services/auth';
import { AuthState, LoginRequest, RegisterRequest, RegisterResponse, User } from '../types/auth';

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context
interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<User | null>;
  register: (userData: RegisterRequest) => Promise<RegisterResponse | void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on mount (ignore stale StrictMode remount results)
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!cancelled) {
          dispatch({ type: 'AUTH_LOGOUT' });
        }
        return;
      }
      // Clear legacy local-only demo sessions
      if (token === 'demo-institution-token') {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('demoInstitutionUser');
        if (!cancelled) {
          dispatch({ type: 'AUTH_LOGOUT' });
        }
        return;
      }
      try {
        const user = await authService.getCurrentUser();
        if (cancelled) {
          return;
        }
        if (user) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token },
          });
        } else {
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } catch {
        if (!cancelled) {
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);
  // Login function - returns user on success so caller can redirect by role
  const login = useMemo(
    () => async (credentials: LoginRequest): Promise<User | null> => {
      dispatch({ type: 'AUTH_START' });
      try {
        const response = await authService.login(credentials);
        localStorage.setItem('token', response.token);
        localStorage.setItem('refreshToken', response.refreshToken);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: response.user, token: response.token },
        });
        return response.user;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Login failed';
        dispatch({
          type: 'AUTH_FAILURE',
          payload: message,
        });
        throw error;
      }
    },
    [dispatch]
  );

  // Register must NOT toggle global isLoading (AUTH_START): AppContent unmounts
  // auth pages while isLoading, which wipes in-progress registration success UI.
  const register = useMemo(
    () => async (userData: RegisterRequest): Promise<RegisterResponse | void> => {
      dispatch({ type: 'CLEAR_ERROR' });
      try {
        const response = await authService.register(userData);
        if (response.requiresEmailConfirmation) {
          // Stay logged out; keep the registration page mounted so caller can show success.
          return response;
        }
        if (response.token && response.user) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('refreshToken', response.refreshToken ?? '');
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user: response.user, token: response.token },
          });
        }
        return response;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Registration failed';
        dispatch({
          type: 'AUTH_FAILURE',
          payload: message,
        });
        throw error; // Rethrow so caller (e.g. RegisterExistingMember) can display the error
      }
    },
    [dispatch]
  );

  // Logout function
  const logout = useMemo(
    () => async () => {
      try {
        await authService.logout();
      } catch (error) {
        console.error('Logout error:', error);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('demoInstitutionUser');
      dispatch({ type: 'AUTH_LOGOUT' });
    },
    [dispatch]
  );

  // Clear error function
  const clearError = useMemo(
    () => () => {
      dispatch({ type: 'CLEAR_ERROR' });
    },
    [dispatch]
  );

  const refreshUser = useMemo(
    () => async (): Promise<User | null> => {
      const token = localStorage.getItem('token');
      if (!token) return null;
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token },
          });
        }
        return user;
      } catch {
        return null;
      }
    },
    [dispatch]
  );

  const value: AuthContextType = useMemo(
    () => ({
      ...state,
      login,
      register,
      logout,
      refreshUser,
      clearError,
    }),
    [state, login, register, logout, refreshUser, clearError]
  );

  return React.createElement(AuthContext.Provider, { value }, children);
};

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
